import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  paidMediaOverviewService,
  type PaidMediaOverviewAttributionConfidenceData,
  type PaidMediaOverviewAttributionMode,
  type PaidMediaOverviewEfficiencyScaleData,
  type PaidMediaOverviewFilterParams,
  type PaidMediaOverviewFiltersData,
  type PaidMediaOverviewFunnelData,
  type PaidMediaOverviewGranularity,
  type PaidMediaOverviewMeta,
  type PaidMediaOverviewPlatformFilter,
  type PaidMediaOverviewRevenueValidationData,
  type PaidMediaOverviewSummaryData,
  type PaidMediaOverviewTrendsData,
  type PaidMediaOverviewWarning,
} from "src/services/PaidMediaOverviewAPI";
import { getApiLangFromI18n } from "src/utils/apiLang";
import {
  activeOverviewPlatforms,
  buildMockCampaignUniverse,
  isMockOnlyPlatform,
  buildPlatformAggregates,
  composeAttributionConfidence,
  composeEfficiencyScale,
  composeOverviewFilters,
  composeOverviewFunnel,
  composeOverviewSummary,
  composeOverviewTrends,
  composeRevenueValidation,
  mockCampaignStatusCodes,
  OVERVIEW_CURRENCY,
  type OverviewCompositionContext,
  type PlatformAggregate,
} from "../utils/paidMediaOverviewComposition";
import { useAsyncResource } from "./useAsyncResource";

export interface UsePaidMediaOverviewParams {
  startDate: string;
  endDate: string;
  granularity: PaidMediaOverviewGranularity;
  platform: PaidMediaOverviewPlatformFilter;
  attributionMode: PaidMediaOverviewAttributionMode;
  campaignId: string;
  objectiveOrNetwork: string;
  status: string;
}

export interface PaidMediaOverviewBundle {
  summary: PaidMediaOverviewSummaryData | null;
  trends: PaidMediaOverviewTrendsData | null;
  funnel: PaidMediaOverviewFunnelData | null;
  efficiencyScale: PaidMediaOverviewEfficiencyScaleData | null;
  revenueValidation: PaidMediaOverviewRevenueValidationData | null;
  attributionConfidence: PaidMediaOverviewAttributionConfidenceData | null;
  filters: PaidMediaOverviewFiltersData | null;
  currency: string | null;
  appliedAttributionMode: PaidMediaOverviewAttributionMode;
  storeRevenueAvailable: boolean;
  aggregates: PlatformAggregate[];
  warnings: PaidMediaOverviewWarning[];
  errors: string[];
  summaryLoading: boolean;
  trendsLoading: boolean;
  funnelLoading: boolean;
  efficiencyScaleLoading: boolean;
  revenueValidationLoading: boolean;
  attributionConfidenceLoading: boolean;
  coreLoading: boolean;
}

const SUPPRESSED_WARNING_CODES = new Set([
  "ATTRIBUTION_MAPPING_UNVERIFIED",
  "CAMPAIGN_VIEW_NOT_SHOPIFY_VALIDATED",
]);

const collectWarnings = (
  metas: Array<PaidMediaOverviewMeta | null | undefined>
): PaidMediaOverviewWarning[] => {
  const seen = new Set<string>();
  const warnings: PaidMediaOverviewWarning[] = [];

  metas.forEach(meta => {
    (meta?.warnings ?? []).forEach(warning => {
      if (!warning?.message || SUPPRESSED_WARNING_CODES.has(warning.code)) {
        return;
      }
      const key = `${warning.code ?? ""}|${warning.message}`;
      if (!seen.has(key)) {
        seen.add(key);
        warnings.push(warning);
      }
    });
  });

  return warnings;
};

const collectErrors = (errors: Array<string | null>): string[] => {
  const seen = new Set<string>();
  return errors.filter((error): error is string => {
    if (!error || seen.has(error)) {
      return false;
    }
    seen.add(error);
    return true;
  });
};

export function usePaidMediaOverview({
  startDate,
  endDate,
  granularity,
  platform,
  attributionMode,
  campaignId,
  objectiveOrNetwork,
  status,
}: UsePaidMediaOverviewParams): PaidMediaOverviewBundle {
  const { t, i18n } = useTranslation();
  const fallbackError = t("paidMediaOverview.errors.requestFailed");
  // The live endpoints only know Meta and Google, so filtering to TikTok or
  // Snapchat skips the request and renders from the mock dataset alone.
  const rangeReady = Boolean(startDate && endDate) && !isMockOnlyPlatform(platform);
  /**
   * Part of every request below *and* of their dependency lists: the overview endpoints return
   * copy the dashboard renders as-is, so switching language has to re-fetch. `useAsyncResource`
   * re-runs when the request callback's identity changes, which is what these deps drive.
   */
  const lang = getApiLangFromI18n(i18n);

  const filterParams = useMemo<PaidMediaOverviewFilterParams>(
    () => ({
      platform,
      campaign_id: campaignId || undefined,
      objective_or_network: objectiveOrNetwork || undefined,
      status: status || undefined,
    }),
    [platform, campaignId, objectiveOrNetwork, status]
  );

  const summaryRequest = useCallback(
    () =>
      paidMediaOverviewService.fetchSummary({
        start_date: startDate,
        end_date: endDate,
        attribution_mode: attributionMode,
        lang,
        ...filterParams,
      }),
    [startDate, endDate, attributionMode, filterParams, lang]
  );

  const trendsRequest = useCallback(
    () =>
      paidMediaOverviewService.fetchTrends({
        start_date: startDate,
        end_date: endDate,
        granularity,
        attribution_mode: attributionMode,
        lang,
        ...filterParams,
      }),
    [startDate, endDate, granularity, attributionMode, filterParams, lang]
  );

  const funnelRequest = useCallback(
    () =>
      paidMediaOverviewService.fetchFunnel({
        start_date: startDate,
        end_date: endDate,
        attribution_mode: attributionMode,
        lang,
        ...filterParams,
      }),
    [startDate, endDate, attributionMode, filterParams, lang]
  );

  const efficiencyScaleRequest = useCallback(
    () =>
      paidMediaOverviewService.fetchEfficiencyScale({
        start_date: startDate,
        end_date: endDate,
        attribution_mode: attributionMode,
        lang,
        ...filterParams,
      }),
    [startDate, endDate, attributionMode, filterParams, lang]
  );

  const revenueValidationRequest = useCallback(
    () =>
      paidMediaOverviewService.fetchRevenueValidation({
        start_date: startDate,
        end_date: endDate,
        lang,
        ...filterParams,
      }),
    [startDate, endDate, filterParams, lang]
  );

  const attributionConfidenceRequest = useCallback(
    () =>
      paidMediaOverviewService.fetchAttributionConfidence({
        start_date: startDate,
        end_date: endDate,
        platform,
        lang,
      }),
    [startDate, endDate, platform, lang]
  );

  const filtersRequest = useCallback(
    () =>
      paidMediaOverviewService.fetchFilters({
        start_date: startDate,
        end_date: endDate,
        lang,
      }),
    [startDate, endDate, lang]
  );

  const summary = useAsyncResource(rangeReady ? summaryRequest : null, fallbackError);
  const trends = useAsyncResource(rangeReady ? trendsRequest : null, fallbackError);
  const funnel = useAsyncResource(rangeReady ? funnelRequest : null, fallbackError);
  const efficiencyScale = useAsyncResource(
    rangeReady ? efficiencyScaleRequest : null,
    fallbackError
  );
  const revenueValidation = useAsyncResource(
    rangeReady ? revenueValidationRequest : null,
    fallbackError
  );
  const attributionConfidence = useAsyncResource(
    rangeReady ? attributionConfidenceRequest : null,
    fallbackError
  );
  const filters = useAsyncResource(rangeReady ? filtersRequest : null, fallbackError);

  const warnings = useMemo(
    () =>
      collectWarnings([
        summary.data?.meta,
        trends.data?.meta,
        funnel.data?.meta,
        efficiencyScale.data?.meta,
        revenueValidation.data?.meta,
        filters.data?.meta,
      ]),
    [
      summary.data,
      trends.data,
      funnel.data,
      efficiencyScale.data,
      revenueValidation.data,
      filters.data,
    ]
  );

  const errors = useMemo(
    () =>
      collectErrors([
        summary.error,
        trends.error,
        funnel.error,
        efficiencyScale.error,
        revenueValidation.error,
        filters.error,
      ]),
    [
      summary.error,
      trends.error,
      funnel.error,
      efficiencyScale.error,
      revenueValidation.error,
      filters.error,
    ]
  );

  const context = useMemo<OverviewCompositionContext>(
    () => ({
      startDate,
      endDate,
      granularity,
      platformFilter: platform,
      attributionMode,
      campaignId,
      objectiveOrNetwork,
      status,
    }),
    [
      startDate,
      endDate,
      granularity,
      platform,
      attributionMode,
      campaignId,
      objectiveOrNetwork,
      status,
    ]
  );

  const universe = useMemo(
    () => buildMockCampaignUniverse(context, activeOverviewPlatforms(context.platformFilter)),
    [context]
  );

  const aggregates = useMemo(
    () =>
      buildPlatformAggregates(
        context,
        { summary: summary.data?.data ?? null, funnel: funnel.data?.data ?? null },
        universe
      ),
    [context, summary.data, funnel.data, universe]
  );

  const composedSummary = useMemo(
    () => composeOverviewSummary(context, aggregates, summary.data?.data ?? null),
    [context, aggregates, summary.data]
  );

  const composedTrends = useMemo(
    () => composeOverviewTrends(context, aggregates, trends.data?.data ?? null),
    [context, aggregates, trends.data]
  );

  const composedFunnel = useMemo(() => composeOverviewFunnel(aggregates), [aggregates]);

  const composedEfficiencyScale = useMemo(
    () =>
      composeEfficiencyScale(
        aggregates,
        universe,
        context,
        efficiencyScale.data?.data?.campaign_view ?? []
      ),
    [aggregates, universe, context, efficiencyScale.data]
  );

  const composedRevenueValidation = useMemo(
    () =>
      composeRevenueValidation(
        aggregates,
        attributionMode,
        revenueValidation.data?.data?.platforms ?? []
      ),
    [aggregates, attributionMode, revenueValidation.data]
  );

  const composedAttributionConfidence = useMemo(
    () => composeAttributionConfidence(aggregates, attributionConfidence.data?.data ?? null),
    [aggregates, attributionConfidence.data]
  );

  const composedFilters = useMemo(
    () =>
      composeOverviewFilters(
        filters.data?.data ?? null,
        universe,
        mockCampaignStatusCodes(universe)
      ),
    [filters.data, universe]
  );

  const hasAggregates = aggregates.length > 0;

  return {
    summary: hasAggregates ? composedSummary : null,
    trends: composedTrends,
    funnel: hasAggregates ? composedFunnel : null,
    efficiencyScale: hasAggregates ? composedEfficiencyScale : null,
    revenueValidation: hasAggregates ? composedRevenueValidation : null,
    attributionConfidence: hasAggregates ? composedAttributionConfidence : null,
    filters: composedFilters,
    currency: OVERVIEW_CURRENCY,
    appliedAttributionMode: attributionMode,
    storeRevenueAvailable: true,
    aggregates,
    warnings,
    errors,
    summaryLoading: summary.loading,
    trendsLoading: trends.loading,
    funnelLoading: funnel.loading,
    efficiencyScaleLoading: efficiencyScale.loading,
    revenueValidationLoading: revenueValidation.loading,
    attributionConfidenceLoading: attributionConfidence.loading,
    coreLoading: summary.loading,
  };
}
